import { useSupabase, useTenant } from "@how/app-kit";
import { useQuery } from "@tanstack/react-query";

/*
 * Read-only figures for the home dashboard. Every read goes through row-level security like the rest of the app, and
 * is filtered to the church on screen. A figure the reader may not see comes back null and is shown as unavailable,
 * never as zero.
 */

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export interface StaffFigures {
  readonly people: number | null;
  /** When each person on file now was added, for the last six months. */
  readonly addedSince: readonly string[] | null;
  readonly households: number | null;
  readonly activeGroups: number | null;
  readonly campuses: readonly { readonly name: string; readonly people: number }[] | null;
}

export function useStaffFigures(sixMonthsAgo: string) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["home-staff-figures", tenantId, sixMonthsAgo],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async (): Promise<StaffFigures> => {
      const id = tenantId ?? "";
      const [people, added, households, groups, campuses, unassigned] = await Promise.all([
        supabase
          .from("people")
          .select("id", { count: "exact", head: true })
          .eq("tenant_id", id)
          .is("deleted_at", null),
        supabase
          .from("people")
          .select("created_at")
          .eq("tenant_id", id)
          .is("deleted_at", null)
          .gte("created_at", sixMonthsAgo)
          .limit(10000),
        supabase
          .from("households")
          .select("id", { count: "exact", head: true })
          .eq("tenant_id", id)
          .is("deleted_at", null),
        supabase
          .from("groups")
          .select("id", { count: "exact", head: true })
          .eq("tenant_id", id)
          .eq("is_active", true),
        supabase
          .from("campuses")
          .select("id, name")
          .eq("tenant_id", id)
          .eq("is_active", true)
          .order("sort_order"),
        supabase
          .from("people")
          .select("id", { count: "exact", head: true })
          .eq("tenant_id", id)
          .is("deleted_at", null)
          .is("primary_campus_id", null),
      ]);
      let split: StaffFigures["campuses"] = null;
      if (!campuses.error && !unassigned.error && campuses.data.length > 0) {
        const counts = await Promise.all(
          campuses.data.map((c) =>
            supabase
              .from("people")
              .select("id", { count: "exact", head: true })
              .eq("tenant_id", id)
              .is("deleted_at", null)
              .eq("primary_campus_id", c.id),
          ),
        );
        if (counts.every((r) => !r.error)) {
          split = [
            ...campuses.data.map((c, i) => ({ name: c.name, people: counts[i]?.count ?? 0 })),
            { name: "No campus", people: unassigned.count ?? 0 },
          ]
            .filter((p) => p.people > 0)
            .sort((a, b) => b.people - a.people);
        }
      }
      return {
        people: people.error ? null : (people.count ?? 0),
        addedSince: added.error ? null : added.data.map((r) => r.created_at),
        households: households.error ? null : (households.count ?? 0),
        activeGroups: groups.error ? null : (groups.count ?? 0),
        campuses: split,
      };
    },
  });
}

export interface UpcomingOccurrence {
  readonly id: string;
  readonly startsAt: string;
  readonly title: string;
  readonly location: string | null;
}

/**
 * The next dates on the calendar (thirty days), soonest first, and how many there are in all. Only open events: a
 * closed one keeps its dates (the Events page hides it under its Active filter), so the join is inner and filtered
 * on the event, or a sample event closed on 2026-10-10 still led "Coming up" (seen on staging, 2026-10-14).
 */
export function useUpcomingOccurrences(now: string, until: string) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["home-staff-upcoming", tenantId, now.slice(0, 13)],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async (): Promise<{ total: number; next: UpcomingOccurrence[] }> => {
      const { data, error, count } = await supabase
        .from("event_occurrences")
        .select("id, starts_at, events!inner(title, location)", { count: "exact" })
        .eq("tenant_id", tenantId ?? "")
        .eq("is_cancelled", false)
        .eq("events.is_active", true)
        .gte("starts_at", now)
        .lt("starts_at", until)
        .order("starts_at")
        .limit(6);
      if (error) throw error;
      return {
        total: count ?? data.length,
        next: data.map((o) => ({
          id: o.id,
          startsAt: o.starts_at,
          title: o.events.title,
          location: o.events.location,
        })),
      };
    },
  });
}
